import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQAccordionProps {
  items: FAQItem[];
}

export function FAQAccordion({ items }: FAQAccordionProps) {
  return (
    // suppressHydrationWarning is needed because Radix UI generates random IDs
    // for accessibility attributes (aria-controls, id) which differ between SSR and client
    //
    // Every answer starts open (`multiple` + `defaultValue` below): the FAQ copy
    // is part of these pages' indexable text, and it is visible on first paint
    // instead of being hidden behind a click.
    //
    // `forceMount` on the content was dropped on 2026-09-26. It kept collapsed
    // answers in the DOM, but it also kept them in the accessibility tree
    // (Radix only sets `hidden` when the content is not force-mounted) and it
    // made collapsing depend on a 0.2s animation with no `fill-mode: forwards`,
    // which is what silently broke the FAQ on /pricing and /templates (every
    // "closed" answer measured 36-56px tall). Radix unmounts a collapsed answer
    // now, which is the shadcn default.
    //
    // `whitespace-pre-line` is load-bearing: the homepage answers carry `\n\n`
    // paragraph breaks and `•` bullet lines (Common.seo.faqs in messages/*.json),
    // and without it they collapse into one run-on line.
    <Accordion
      // `multiple` (not `single`): every answer starts open, and each question can
      // be collapsed on its own without closing the others.
      type="multiple"
      defaultValue={items.map((_, index) => `item-${index}`)}
      className="w-full"
      suppressHydrationWarning
    >
      {items.map((item, index) => (
        <AccordionItem
          key={index}
          value={`item-${index}`}
          suppressHydrationWarning
        >
          <AccordionTrigger
            className="text-left font-medium text-gray-900 dark:text-white hover:no-underline hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            suppressHydrationWarning
          >
            {item.question}
          </AccordionTrigger>
          <AccordionContent
            className="whitespace-pre-line text-gray-600 dark:text-gray-300"
            suppressHydrationWarning
          >
            {item.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
